/* eslint-disable no-eval */
import algoliasearchHelper from 'algoliasearch-helper';
import { ApolloLink, Observable } from 'apollo-link';
import { addTypenameToDocument, getMainDefinition, hasDirectives } from 'apollo-utilities';
import { graphql } from 'graphql-anywhere/lib/async';
import algoliasearch from 'algoliasearch';
import { Base64 } from 'utils/encoding';
import { bugSnagReport } from 'utils/utils';
import get from 'lodash/get';

const parameters = [
    'advancedSyntax',
    'allowTyposOnNumericTokens',
    'analytics',
    'analyticsTags',
    'aroundLatLng',
    'aroundLatLngViaIP',
    'aroundPrecision',
    'aroundRadius',
    'attributesToHighlight',
    'attributesToRetrieve',
    'attributesToSnippet',
    'disjunctiveFacets',
    'distinct',
    'facets',
    'filters',
    'getRankingInfo',
    'hitsPerPage',
    'ignorePlurals',
    'insideBoundingBox',
    'insidePolygon',
    'maxValuesPerFacet',
    'minimumAroundRadius',
    'minWordSizefor1Typo',
    'minWordSizefor2Typos',
    'optionalWords',
    'page',
    'query',
    'queryType',
    'removeWordsIfNoResults',
    'replaceSynonymsInHighlight',
    'restrictSearchableAttributes',
    'synonyms',
    'tagFilters',
    'typoTolerance',
];

const addTypeNameToResult = (result, __typename = 'AlgoliaQuery') => Object.assign({}, result.content, { __typename });

const resolver = async (fieldName, root, args, context, info) => {
    const { directives, isLeaf, resultKey } = info;

    const isNotAlgoliaQuery = !directives || !directives.algolia;
    if (isLeaf || isNotAlgoliaQuery) {
        const returnValue = (root || {})[resultKey] || (root || {})[fieldName];
        return returnValue !== undefined ? returnValue : null;
    }

    if (!directives.algolia.index) {
        throw new Error('Algolia index name is required');
    }

    const helper = algoliasearchHelper(context.client, directives.algolia.index);

    parameters.forEach((parameter) => {
        if (parameter in directives.algolia) {
            helper.setQueryParameter(parameter, directives.algolia[parameter]);
        }
    });
    let response;
    if (directives.algolia['page']) {
        response = await helper
            .setPage(directives.algolia['page'])
            .searchOnce()
            .then((result) => addTypeNameToResult(result));
    } else {
        response = await helper
            .searchOnce()
            .then(async (result) => addTypeNameToResult(result, directives.algolia['__typename']));
    }
    const popupate = directives.algolia['populate']
        ? JSON.parse(Base64.decode(directives.algolia['populate'])).map((p) => ({
            ...p,
            getIds: eval(p.getIds),
            skip: eval(p.skip),
        }))
        : null;
    if (popupate && popupate.length) {
        const client = algoliasearch(
            process.env.REACT_APP_ALGOLIA_ID as string,
            (localStorage.getItem('ALGOLIA_TOKEN') as string) ||
                (process.env.REACT_APP_ALGOLIA_ANONYMOUS_SEARCH_KEY as string),
        );
        response.hits = await Promise.all(
            response.hits.map(async (hit) => {
                const extendsPropsArray = await Promise.all(
                    popupate
                        .filter((p) => !p.skip(hit))
                        .map(async (p) => {
                            try {
                                const index = client.initIndex(p.indexes);
                                const data = await index.getObjects(p.getIds(hit));
                                return { [p.name]: data?.results };
                            } catch (err) {
                                return {};
                            }
                        }),
                );
                return {
                    ...hit,
                    extendsProps: { ...Object.assign({}, ...extendsPropsArray) },
                };
            }),
        );
    }
    return response;
};

class AlgoliaLink extends ApolloLink {
    getClient: any;
    constructor({ getClient }) {
        super();
        this.getClient = getClient;
    }

    request(operation, forward) {
        const isAlgoliaQuery = hasDirectives(['algolia'], operation.query);

        if (!isAlgoliaQuery && forward) {
            return forward(operation);
        }
        const reqContext = operation.getContext();
        if (
            get(reqContext, ['scope']) === 'private' &&
            !get(this.getClient(), ['transporter', 'headers', 'x-algolia-api-key'])
        ) {
            return new Observable((observer) => {
                observer.error(new Error('Access denied'));
            });
        }

        const context = {
            client: this.getClient(),
            findType: (directives) => directives.algolia.type,
            mainDefinition: getMainDefinition(operation.query), // note: not used at the moment
        };

        return new Observable((observer) => {
            graphql(resolver, addTypenameToDocument(operation.query), {}, context, operation.variables)
                .then((data) => {
                    observer.next({ data });
                    observer.complete();
                })
                .catch((error) => {
                    const bugsnagMetadata = {
                        index: operation.variables.index,
                        query: operation.variables.query,
                        filters: operation.variables.filters,
                        facets: operation.variables.facets,
                    };
                    bugSnagReport(error, 'AlgoliaLink Request', bugsnagMetadata);
                    observer.error(error);
                });
        });
    }
}

let algoliaLink: AlgoliaLink;

const getAlgoliaLink = () => {
    if (!algoliaLink) {
        algoliaLink = new AlgoliaLink({
            getClient: () =>
                algoliasearch(
                    process.env.REACT_APP_ALGOLIA_ID as string,
                    (localStorage.getItem('ALGOLIA_TOKEN') as string) ||
                        (process.env.REACT_APP_ALGOLIA_ANONYMOUS_SEARCH_KEY as string),
                ),
        });
    }
    return algoliaLink;
};
export default getAlgoliaLink;
